import { ref, watch, type Ref } from 'vue'
import { usePrefixedStorage } from './usePrefixedStorage'

export type PriceZone = 'DK1' | 'DK2'

const VALID_ZONES: readonly PriceZone[] = ['DK1', 'DK2'] as const

function isPriceZone(value: unknown): value is PriceZone {
  return typeof value === 'string' && (VALID_ZONES as readonly string[]).includes(value)
}

export interface UseZoneOptions {
  /** Storage namespace — must match the widget slug to avoid cross-widget collisions. */
  widget: string
  /** Default zone if nothing is persisted and no URL parameter overrides it. */
  default?: PriceZone
  /**
   * If set, on init the composable reads `?<urlParam>=DK1|DK2` from
   * `window.location.search` and uses it as the initial value. The URL value
   * takes precedence over both the persisted value and the default.
   */
  urlParam?: string
}

export interface UseZoneReturn {
  zone: Ref<PriceZone>
  setZone: (next: PriceZone) => void
}

function readUrlParam(name: string): PriceZone | null {
  if (typeof window === 'undefined') return null
  try {
    const value = new URLSearchParams(window.location.search).get(name)
    return isPriceZone(value) ? value : null
  } catch {
    return null
  }
}

export function useZone(options: UseZoneOptions): UseZoneReturn {
  const { widget, default: defaultZone = 'DK1', urlParam } = options
  const storage = usePrefixedStorage(widget, 'session')

  const fromUrl = urlParam ? readUrlParam(urlParam) : null
  const fromStorage = storage.get<PriceZone>('zone')
  const initial: PriceZone = fromUrl ?? (isPriceZone(fromStorage) ? fromStorage : defaultZone)

  const zone = ref<PriceZone>(initial)
  storage.set('zone', zone.value)

  watch(zone, (next) => {
    storage.set('zone', next)
  })

  function setZone(next: PriceZone): void {
    if (!isPriceZone(next)) return
    zone.value = next
  }

  return { zone, setZone }
}
